import { Sidebar, useSidebar } from '@/components/Sidebar';
import { PageHeader } from '@/components/PageHeader';
import { useTranslation } from '@/hooks/useTranslation';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import { Bot, Check } from 'lucide-react';
import AiAssistantPage from '@/pages/AiAssistantPage';

export default function CoachPage() {
  const { t } = useTranslation();
  const { isEnabled } = useFeatureFlags();
  const { collapsed: sidebarCollapsed } = useSidebar();

  if (isEnabled('ai_chat')) {
    return <AiAssistantPage />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Sidebar activePage="coach" />
      <main className={sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}>
        <PageHeader title={t('coach.title')} subtitle={t('coach.subtitle')} />

        <div className="max-w-5xl mx-auto px-6 py-8 lg:px-8 lg:py-10">
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="border-b border-gray-100 px-6 py-5 dark:border-gray-800 sm:px-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-[#16c47f] dark:bg-emerald-950/40">
                  <Bot className="h-5 w-5" strokeWidth={2.25} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">
                    {t('coach.title')}
                  </h2>
                  <span className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    {t('common.comingSoon')}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 py-8 sm:px-8 sm:py-10">
              <div className="max-w-2xl">
                <h1 className="text-2xl font-bold leading-tight text-gray-900 dark:text-white sm:text-3xl">
                  {t('coach.comingSoon.title')}
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-6 text-gray-600 dark:text-gray-300 sm:text-base">
                  {t('coach.comingSoon.description')}
                </p>
              </div>

              <div className="mt-8 grid gap-3 border-t border-gray-100 pt-6 dark:border-gray-800 sm:grid-cols-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Check className="h-4 w-4 flex-none text-[#16c47f]" strokeWidth={2.5} />
                  {t('coach.features.analysis')}
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Check className="h-4 w-4 flex-none text-[#16c47f]" strokeWidth={2.5} />
                  {t('coach.features.insights')}
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Check className="h-4 w-4 flex-none text-[#16c47f]" strokeWidth={2.5} />
                  {t('coach.features.decisions')}
                </div>
              </div>

              <p className="mt-8 text-xs leading-5 text-gray-500 dark:text-gray-400">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {t('coach.disclaimer.title')}
                </span>{' '}
                {t('coach.disclaimer.description')}
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
